<template>
    <div class="app-container">
        <!-- 左侧目录区域 -->
        <div class="sidebar">
            <div class="logo-container">
                <div class="logo">
                    <i class="fas fa-book"></i>
                    数学题库组卷系统
                </div>
            </div>

            <div class="directory-tree">
                <!-- 教材版本选择器 -->
                <div class="selector-section">
                    <label>教材版本:</label>
                    <select v-model="selectedVersion" @change="onVersionChange" class="version-selector">
                        <option v-for="version in textbookVersions" :key="version.id" :value="version.id">
                            {{ version.name }}
                        </option>
                    </select>
                </div>

                <!-- 教材选择器 -->
                <div class="selector-section">
                    <label>教材:</label>
                    <select v-model="selectedTextbook" @change="onTextbookChange" class="textbook-selector">
                        <option v-for="textbook in filteredTextbooks" :key="textbook.id" :value="textbook.id">
                            {{ textbook.name }}
                        </option>
                    </select>
                </div>

                <!-- 章节树 -->
                <div class="textbook-title">
                    <i class="fas fa-book"></i>
                    章节目录
                </div>
                <div v-for="chapter in chapters" :key="chapter.id" class="chapter">
                    <div @click="selectChapter(chapter)" class="chapter-header"
                        :class="{ collapsed: chapter.collapsed, active: activeChapter === chapter.id }">
                        <i class="fas fa-chevron-down" @click.stop="chapter.collapsed = !chapter.collapsed"></i>
                        <span>{{ chapter.title }}</span>
                    </div>
                    <div class="sections" v-show="!chapter.collapsed">
                        <div v-for="section in chapter.sections" :key="section.id" class="section"
                            :class="{ active: activeSection === section.id }" @click="selectSection(section)">
                            {{ section.name }}
                            <div class="tags" v-if="section.knowledgePoints && section.knowledgePoints.length">
                                <span v-for="kp in section.knowledgePoints" :key="kp.id" class="tag"
                                    :class="{ active: activeKnowledgePoint === kp.id }"
                                    @click.stop="selectKnowledgePoint(kp)">
                                    {{ kp.name }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 右侧主内容区域 -->
        <div class="main-content">
            <!-- 顶部搜索栏 -->
            <div class="top-bar">
                <div class="search-box">
                    <i class="fas fa-search" @click="triggerSearch"></i>
                    <input type="text" placeholder="搜索题目、知识点..." v-model="searchQuery"
                        @keyup.enter="triggerSearch">
                </div>
                <!-- <div class="textbook-selector filter-group">
                        <select v-model="currentTextbook">
                            <option v-for="textbook in textbooks" :value="textbook.id">
                                {{ textbook.name }}
                            </option>
                        </select>
                    </div> -->
                <div class="additional-options">
                    <label class="checkbox-option">
                        <input type="checkbox" v-model="onlyNew"> 只看新题
                    </label>
                    <label class="checkbox-option">
                        <input type="checkbox" v-model="showAnswer"> 显示答案
                    </label>
                </div>
            </div>

            <!-- 筛选区域 -->
            <div class="filter-section" :class="{ 'filter-collapsed': filterCollapsed }">
                <div class="filter-title">
                    <div class="filter-title-left">
                        <i class="fas fa-filter"></i>
                        筛选条件
                        <span class="clear-filters" @click="clearFilters" v-if="hasActiveFilters">清除筛选</span>
                    </div>
                    <div class="filter-title-right">
                        <span class="filter-toggle-btn" @click="filterCollapsed = !filterCollapsed">
                            <i :class="filterCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"></i>
                            {{ filterCollapsed ? '展开筛选' : '收起筛选' }}
                        </span>
                    </div>
                </div>

                <div class="filter-body" v-show="!filterCollapsed">
                    <!-- 常用筛选（默认展示） -->
                    <!-- 题型筛选 -->
                    <div class="filter-row">
                        <span class="filter-label">题型:</span>
                        <div class="filter-pills">
                            <span class="filter-pill" :class="{ active: filter.type === 'all' }"
                                @click="filter.type = 'all'">全部</span>
                            <span v-for="t in questionTypes" :key="t" class="filter-pill"
                                :class="{ active: filter.type === t }" @click="filter.type = t">{{ t }}</span>
                        </div>
                    </div>

                    <!-- 难度筛选 -->
                    <div class="filter-row">
                        <span class="filter-label">难度:</span>
                        <div class="filter-pills">
                            <span class="filter-pill" :class="{ active: filter.difficult === 'all' }"
                                @click="filter.difficult = 'all'">全部</span>
                            <span v-for="d in difficulties" :key="d" class="filter-pill"
                                :class="{ active: filter.difficult === d }" @click="filter.difficult = d">{{ d
                                }}</span>
                        </div>
                    </div>

                    <!-- 年级筛选 -->
                    <div class="filter-row">
                        <span class="filter-label">年级:</span>
                        <div class="filter-pills">
                            <span class="filter-pill" :class="{ active: filter.grades === 'all' }"
                                @click="filter.grades = 'all'">全部</span>
                            <span v-for="g in grades" :key="g" class="filter-pill"
                                :class="{ active: filter.grades === g }" @click="filter.grades = g">{{ g }}</span>
                        </div>
                    </div>

                    <!-- 场景筛选 -->
                    <div class="filter-row">
                        <span class="filter-label">场景:</span>
                        <div class="filter-pills">
                            <span class="filter-pill" :class="{ active: filter.scene === 'all' }"
                                @click="filter.scene = 'all'">全部</span>
                            <span v-for="s in scenes" :key="s" class="filter-pill"
                                :class="{ active: filter.scene === s }" @click="filter.scene = s">{{ s }}</span>
                        </div>
                    </div>

                    <!-- 高级筛选（默认折叠） -->
                    <div class="advanced-filter-section">
                        <div class="advanced-filter-toggle" @click="advancedFilterExpanded = !advancedFilterExpanded">
                            <span class="advanced-filter-label">
                                <i :class="advancedFilterExpanded ? 'fas fa-caret-down' : 'fas fa-caret-right'"></i>
                                高级筛选
                            </span>
                            <span class="advanced-filter-hint" v-if="hasAdvancedFilters">
                                (已启用)
                            </span>
                        </div>

                        <div class="advanced-filter-body" v-show="advancedFilterExpanded">
                            <!-- 年份筛选 -->
                            <div class="filter-row">
                                <span class="filter-label">年份:</span>
                                <div class="filter-pills">
                                    <span class="filter-pill" :class="{ active: filter.year === 'all' }"
                                        @click="filter.year = 'all'">全部</span>
                                    <span v-for="y in years" :key="y" class="filter-pill"
                                        :class="{ active: filter.year === y }" @click="filter.year = y">{{ y
                                        }}</span>
                                </div>
                            </div>

                            <!-- 地区筛选 -->
                            <div class="filter-row filter-row-collapsible">
                                <span class="filter-label">地区:</span>
                                <div class="filter-pills-wrapper">
                                    <div class="filter-pills" :class="{ collapsed: !areaExpanded }">
                                        <span class="filter-pill" :class="{ active: filter.area === 'all' }"
                                            @click="filter.area = 'all'">全部</span>
                                        <span v-for="a in areas" :key="a" class="filter-pill"
                                            :class="{ active: filter.area === a }" @click="filter.area = a">{{ a
                                            }}</span>
                                    </div>
                                    <span class="expand-btn" @click="areaExpanded = !areaExpanded">
                                        {{ areaExpanded ? '收起' : '展开更多' }}
                                        <i :class="areaExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="stats">
                    <i class="fas fa-database"></i>
                    共计 {{ total }} 道试题
                </div>
            </div>

            <!-- 题目列表区域 -->
            <div class="questions-container">
                <div class="questions-header">
                    <div class="questions-title">
                        <i class="fas fa-list"></i> 题目列表
                    </div>
                    <div class="sort-options">
                        排序:
                        <select v-model="sortOption">
                            <option value="newest">最新</option>
                            <option value="hottest">最热</option>
                            <option value="difficulty">难度</option>
                        </select>
                    </div>
                </div>

                <div class="question-list" v-if="questions.length">
                    <div v-for="(question, index) in questions" :key="question.id"
                        class="question-card tk-quest-item quesroot">
                        <!-- Top Metadata Area -->
                        <div class="ques-additional">
                            <div class="top-msg">
                                <span class="addi-msg ques-src">{{ formatCornerTag(question) }}</span>
                            </div>

                            <div class="msg-box">
                                <div class="left-msg">
                                    <span class="addi-info">
                                        <span class="info-cnt">{{ question.typeName || '未知题型' }}</span>
                                    </span>
                                    <b class="gap">|</b>
                                    <span class="addi-info">
                                        <span class="info-cnt">{{ question.difficultName || '适中' }}({{
                                            getDifficultyNumber(question.difficultName) }})</span>
                                    </span>
                                    <template v-if="question.knowledgePoints && question.knowledgePoints.length">
                                        <b class="gap">|</b>
                                        <div class="knowledge-list-wrapper single-line">
                                            <div class="knowledge-list">
                                                <a v-for="(kp, kpIdx) in question.knowledgePoints" :key="kp.id"
                                                    class="knowledge-item">
                                                    {{ kp.name }}
                                                </a>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                                <div class="right-msg">
                                    <span class="special-tag"
                                        v-if="question.source && question.source.includes('名校')">名校</span>
                                    <span class="special-tag default-blue">解题方法</span>
                                </div>
                            </div>
                        </div>

                        <!-- Question Content Area -->
                        <div class="wrapper quesdiv">
                            <div class="exam-item__cnt" @click="toggleAnswer(question)">
                                <div class="question-content-wrapper">
                                    <span class="question-index">{{ index + 1 }}.</span>
                                    <div class="question-body">
                                        <md-editor preview-only preview-theme="github" v-model="question.content"
                                            style="text-align: left;" />
                                        <div v-if="question.typeName === '单选题' || question.typeName === '多选题'">
                                            <div :class="getLayoutClass(question)" class="option-container">
                                                <div v-for="(option, index) in question.options" :key="index"
                                                    class="option-item-wrapper">
                                                    <span class="option-label">{{ option.label }}.</span>
                                                    <div class="option-content">
                                                        <md-editor preview-only preview-theme="github"
                                                            :model-value="option.content"
                                                            style="background: transparent;" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="showAnswer || question.showAnswer" class="exam-item__opt answer-section">
                                <div class="answer-title"><i class="fas fa-lightbulb"></i> 答案</div>
                                <div class="answer-content">
                                    <md-editor preview-only preview-theme="github" v-model="question.answer"
                                        style="text-align: left;" />
                                </div>
                                <div class="answer-title"><i class="fas fa-lightbulb"></i> 解析</div>
                                <div class="answer-content">
                                    <md-editor preview-only preview-theme="github" v-model="question.explanation"
                                        style="text-align: left;" />
                                </div>
                            </div>
                        </div>

                        <!-- Footer Area -->
                        <div class="exam-item__info clearfix">
                            <div class="info-msgs">
                                <span class="no-bound" title="今日">今日</span>
                                <b class="gap">|</b>
                                <span :title="`${question.usageCount || 0}次组卷`">{{ question.usageCount || 0 }}次组卷</span>
                                <b class="gap">|</b>
                                <span class="look-more-src">
                                    <span class="info-txt font-item-ligth source-text">
                                        引用试卷：
                                        <template v-if="question.paperQuestions && question.paperQuestions.length > 0">
                                            <span v-for="(pq, pqIdx) in question.paperQuestions" :key="pq.id">
                                                <router-link :to="`/papers/${pq.paperId}`" class="paper-link"
                                                    target="_blank">{{ pq.paper?.name || '未知试卷' }}</router-link>
                                                <span v-if="pqIdx < question.paperQuestions.length - 1">, </span>
                                            </span>
                                        </template>
                                        <template v-else>
                                            {{ question.source || '暂无来源' }}
                                        </template>
                                    </span>
                                </span>
                            </div>

                            <div class="ctrl-box">
                                <a class="change ctrl-btn btn_ques_similarq" href="javascript:void(0)"
                                    @click="showSimilarQuestions(question)">相似题</a>
                                <a class="errorques ctrl-btn btn_ques_error" href="javascript:void(0)"
                                    @click="openErrorReport(question)">纠错</a>
                                <a class="detail ctrl-btn" @click="toggleAnswer(question)">详情</a>
                                <a class="fav ctrl-btn btn_ques_collect" @click="openListDialog(question)">收藏</a>
                                <a v-if="isAdmin" class="action-btn text-btn edit-btn ctrl-btn"
                                    style="padding: 4px 12px; margin: 0;" @click="goToEditQuestion(question)">编辑</a>
                                <a class="addques add-exam-btn ctrl-btn btn_ques"
                                    :class="{ 'is-in-basket': isInBasket(question) }"
                                    @click="toggleBasket(question, $event)">
                                    <i :class="isInBasket(question) ? 'fas fa-check' : 'fas fa-shopping-cart'"
                                        style="margin-right: 4px;"></i>
                                    {{ isInBasket(question) ? '已在试题篮' : '加入试题篮' }}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="empty-questions">
                    <i class="fas fa-inbox"></i>
                    <p>暂无题目</p>
                    <span>试试调整筛选条件，或切换左侧章节</span>
                </div>

                <!-- 分页控件 -->
                <div class="bottom-actions">
                    <!-- <div class="practice-options">
                            <div class="practice-btn primary-btn" @click="$router.push('/paper')">
                                <i class="fas fa-bolt"></i> 试卷篮
                            </div>
                            <div class="practice-btn secondary-btn">
                                <i class="fas fa-download"></i> 导出题目
                            </div>
                        </div> -->

                    <!-- <div class="pagination">
                            <div class="pagination-btn" @click="prevPage">
                                <i class="fas fa-chevron-left"></i>
                            </div>
                            <div v-for="page in totalPages" :key="page" class="pagination-btn"
                                :class="{ active: currentPage === page }" @click="currentPage = page">
                                {{ page }}
                            </div>
                            <div class="pagination-btn" @click="nextPage">
                                <i class="fas fa-chevron-right"></i>
                            </div>
                        </div> -->
                    <!-- 分页组件 -->
                    <el-pagination background layout="prev, pager, next, jumper, ->, total" :total="total"
                        :page-size="pageSize" :current-page="page" @current-change="handlePageChange" />
                </div>
            </div>
        </div>
    </div>

    <!-- Floating Question Basket Button -->
    <div class="floating-basket" @click="basketDrawerVisible = true">
        <div class="basket-icon">
            <i class="fas fa-shopping-cart"></i>
        </div>
        <div class="basket-count" v-if="paperBasket.length > 0">{{ paperBasket.length }}</div>
        <div class="basket-label">试题篮</div>
    </div>

    <!-- Question Basket Drawer -->
    <el-drawer v-model="basketDrawerVisible" :with-header="false" direction="rtl" size="400px" class="apple-drawer">
        <div class="drawer-header">
            <div class="drawer-title">试题篮</div>
            <div class="drawer-close" @click="basketDrawerVisible = false">
                <i class="fas fa-times"></i>
            </div>
        </div>
        <div class="basket-drawer-content">
            <div v-if="paperBasket.length === 0" class="empty-basket">
                <i class="fas fa-inbox"></i>
                <p>试题篮为空</p>
                <span>请从题目列表添加题目</span>
            </div>
            <div v-else>
                <div class="basket-summary">
                    <span>已选择 <strong>{{ paperBasket.length }}</strong> 道题目</span>
                    <div class="clear-btn-apple" @click="clearBasket">
                        <i class="fas fa-trash-alt"></i> 清空
                    </div>
                </div>
                <div class="basket-items">
                    <TransitionGroup name="basket-list">
                        <div v-for="(q, index) in paperBasket" :key="q.id" class="basket-item">
                            <div class="basket-item-header">
                                <span class="basket-item-num">{{ index + 1 }}</span>
                                <span class="basket-item-type">{{ getQuestionTypeText(q.type) }}</span>
                                <span class="basket-item-diff">{{ getDifficultyText(q.difficult) }}</span>
                                <div class="remove-btn-apple" @click="removeFromBasket(index)">
                                    <i class="fas fa-minus-circle"></i>
                                </div>
                            </div>
                            <div class="basket-item-content">
                                {{ truncateContent(q.content, 80) }}
                            </div>
                        </div>
                    </TransitionGroup>
                </div>
                <div class="basket-actions">
                    <el-button type="primary" size="large" @click="goToPaperComposition">
                        <i class="fas fa-file-alt"></i> 前往组卷
                    </el-button>
                </div>
            </div>
        </div>
    </el-drawer>

    <SelectListDialog v-model="showListDialog" :questionId="selectedQuestionForList?.id" />

    <!-- Similar Questions Dialog -->
    <el-dialog v-model="showSimilarDialog" title="相似题" width="700px" append-to-body destroy-on-close>
        <div v-loading="loadingSimilar">
            <div v-if="similarQuestions.length === 0 && !loadingSimilar"
                style="text-align:center; padding:20px; color:#999;">
                暂无相似题目
            </div>
            <div v-for="(sq, idx) in similarQuestions" :key="sq.id" class="similar-question-item">
                <div class="similar-q-header">
                    <span class="similar-q-meta">{{ sq.typeName || '' }} | {{ sq.difficultName || '适中' }}</span>
                </div>
                <div class="question-content-wrapper">
                    <span class="question-index">{{ idx + 1 }}.</span>
                    <div class="question-body">
                        <md-editor preview-only preview-theme="github" v-model="sq.content" style="text-align:left;" />
                    </div>
                </div>
                <div class="similar-q-actions">
                    <el-button size="small" type="primary" link @click="toggleAnswer(sq)">{{ sq.showAnswer ? '收起解析' :
                        '查看解析'
                    }}</el-button>
                    <el-button size="small" type="success" link @click="toggleBasket(sq, $event)">{{ isInBasket(sq) ?
                        '已在试题篮' : '加入试题篮' }}</el-button>
                </div>
                <div v-if="sq.showAnswer"
                    style="background:#fafafa; padding:12px; border-radius:4px; margin-top:8px; border:1px solid #f0f0f0;">
                    <div style="font-weight:bold; font-size:13px; color:#666; margin-bottom:4px;">答案</div>
                    <md-editor preview-only preview-theme="github" v-model="sq.answer" style="text-align:left;" />
                    <div style="font-weight:bold; font-size:13px; color:#666; margin-top:8px; margin-bottom:4px;">解析
                    </div>
                    <md-editor preview-only preview-theme="github" v-model="sq.explanation" style="text-align:left;" />
                </div>
            </div>
        </div>
    </el-dialog>

    <!-- Error Report Dialog -->
    <el-dialog v-model="showErrorDialog" title="题目纠错" width="480px" append-to-body destroy-on-close>
        <el-form label-position="top">
            <el-form-item label="错误类型">
                <el-radio-group v-model="errorForm.type">
                    <el-radio label="答案错误">答案错误</el-radio>
                    <el-radio label="解析错误">解析错误</el-radio>
                    <el-radio label="题目不完整">题目不完整</el-radio>
                    <el-radio label="选项错误">选项错误</el-radio>
                    <el-radio label="其他">其他</el-radio>
                </el-radio-group>
            </el-form-item>
            <el-form-item label="错误描述">
                <el-input v-model="errorForm.description" type="textarea" :rows="4"
                    placeholder="请描述具体错误内容，以便我们尽快修正..." />
            </el-form-item>
        </el-form>
        <template #footer>
            <el-button @click="showErrorDialog = false">取消</el-button>
            <el-button type="primary" :disabled="!errorForm.type" @click="submitErrorReport">提交纠错</el-button>
        </template>
    </el-dialog>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, reactive, computed, watch } from 'vue'
import api, { getUser, getTextbookVersions } from '@/utils/api'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import MdEditor from 'md-editor-v3';
import 'md-editor-v3/lib/style.css';
import { ElNotification, ElMessageBox } from 'element-plus'
import SelectListDialog from '../components/SelectListDialog.vue'

const router = useRouter()
const store = useStore()

// Check if current user is admin
const isAdmin = computed(() => {
    const user = getUser()
    return user && user.role === 'admin'
})

// Get current user ID (returns null if not logged in)
const currentUserId = computed(() => {
    const user = getUser()
    return user ? user.id : null
})

// Check login status and redirect if not logged in
const requireLogin = () => {
    if (!currentUserId.value) {
        ElMessageBox.confirm('添加试题篮需要登录账号，是否前往登录？', '请先登录', {
            confirmButtonText: '去登录',
            cancelButtonText: '取消',
            type: 'warning',
        }).then(() => {
            // Clear any potentially invalid token/user data via store before redirecting
            store.commit('CLEAR_AUTH')
            router.push(`/login?redirect=${encodeURIComponent(router.currentRoute.value.fullPath)}`)
        }).catch(() => {
            // User cancelled, do nothing
        })
        return false
    }
    return true
}

// Navigate to admin question manager with question ID
const goToEditQuestion = (question) => {
    router.push({ path: '/admin/questions', query: { editId: question.id } })
}

// --- Error Report Feature ---
const showErrorDialog = ref(false)
const errorReportQuestionId = ref(null)
const errorForm = reactive({
    type: '',
    description: ''
})

const openErrorReport = (question) => {
    errorReportQuestionId.value = question.id
    errorForm.type = ''
    errorForm.description = ''
    showErrorDialog.value = true
}

const submitErrorReport = async () => {
    try {
        await api.post('/api/feedbacks', {
            questionId: errorReportQuestionId.value,
            type: errorForm.type,
            content: errorForm.description
        })
        ElNotification({
            title: '纠错已提交',
            message: `感谢您的反馈！我们将尽快处理题目 #${errorReportQuestionId.value} 的错误报告。`,
            type: 'success',
            duration: 3000
        })
        showErrorDialog.value = false
    } catch (err) {
        ElNotification({ title: '提交失败', message: '请稍后再试', type: 'error' })
    }
}

// --- Similar Questions Feature ---
const showSimilarDialog = ref(false)
const loadingSimilar = ref(false)
const similarQuestions = ref([])

const showSimilarQuestions = async (question) => {
    showSimilarDialog.value = true
    loadingSimilar.value = true
    similarQuestions.value = []
    try {
        // Use the first knowledge point to find similar questions
        const kpId = question.knowledgePoints?.[0]?.id
        const params = {
            page: 1,
            page_size: 10,
            type: question.type || 0,
        }
        if (kpId) {
            params.knowledgePointId = kpId
        }
        const res = await api.get('/api/questions', { params })
        // Filter out the original question itself
        const results = (res.data.questions || []).filter(q => q.id !== question.id)
        similarQuestions.value = results.map(q => ({ ...q, showAnswer: false }))
    } catch (err) {
        console.error('Failed to fetch similar questions:', err)
    } finally {
        loadingSimilar.value = false
    }
}

// Format the top-left corner tag, e.g. "24-25·高一上·江苏南京·期末"
const SCENE_KEYWORDS = ['月考', '期中', '期末', '模拟', '真题', '强基', '竞赛', '单元测', '作业']

const formatAcademicYear = (yearVal) => {
    const raw = String(yearVal || '').trim()
    if (!raw) return ''
    if (/^20\d{2}$/.test(raw)) {
        const yearNum = Number(raw)
        return `${(yearNum - 1).toString().slice(-2)}-${yearNum.toString().slice(-2)}`
    }
    return raw
}

const normalizeSemester = (semesterVal) => {
    const raw = String(semesterVal || '').trim()
    if (!raw) return ''
    if (raw === '上' || raw.includes('上')) return '上'
    if (raw === '下' || raw.includes('下')) return '下'
    return ''
}

const collectSceneTexts = (question) => {
    const texts = [question.scene, question.source]
    ;(question.paperQuestions || []).forEach((pq) => {
        texts.push(pq?.paper?.category)
        texts.push(pq?.paper?.name)
    })
    return texts.filter(Boolean).map(t => String(t))
}

const detectScene = (question) => {
    const texts = collectSceneTexts(question)
    for (const text of texts) {
        const normalized = text.replace(/\s+/g, '')
        const hit = SCENE_KEYWORDS.find(scene => normalized.includes(scene))
        if (hit) return hit
    }
    return ''
}

const detectGrade = (question) => {
    const direct = String(question.gradeName || '').trim()
    if (direct) return direct
    const texts = collectSceneTexts(question)
    for (const text of texts) {
        const hit = text.match(/高[一二三123]/)
        if (hit) return hit[0].replace('1', '一').replace('2', '二').replace('3', '三')
    }
    return ''
}

const formatCornerTag = (question) => {
    const parts = []

    const academicYear = formatAcademicYear(question.year || question.Year)
    if (academicYear) parts.push(academicYear)

    const grade = detectGrade(question)
    const semester = normalizeSemester(question.semester)
    if (grade) parts.push(semester ? `${grade}${semester}` : grade)

    const region = String(question.region || '').trim()
    const city = String(question.city || '').trim()
    const location = region && city ? (region.includes(city) ? region : `${region}${city}`) : (region || city)
    if (location) parts.push(location)

    const scene = detectScene(question)
    if (scene) parts.push(scene)

    return parts.join('·') || '题库题'
}

// Map difficulty name to a rough number mapping for display (e.g., 适中(0.65))
const getDifficultyNumber = (diffName) => {
    const map = {
        '容易': '0.85',
        '较易': '0.75',
        '适中': '0.65',
        '较难': '0.45',
        '困难': '0.30'
    }
    return map[diffName] || '0.65'
}


const downloadFile = async () => {
    try {
        const data = {
            // questions: [],
            questions: questions.value.slice(-2),
            showSource: false,
            answerAttach: false
        }
        console.log(data);
        const res = await api.post('/api/downloadDocx', data, {
            responseType: 'blob',
        })

        const blob = new Blob([res.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url

        // 可选：从后端响应头中提取文件名
        const disposition = res.headers['content-disposition']
        let fileName = 'downloaded-file'
        if (disposition) {
            const match = disposition.match(/filename="?([^"]+)"?/)
            if (match && match[1]) {
                fileName = decodeURIComponent(match[1])
            }
        }

        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
    } catch (err) {
        console.error('下载失败:', err)
    }
}

const optionLabels = ['A', 'B', 'C', 'D'];

const getLayoutClass = (question) => {
    if (!question.options) return 'layout-4-line';

    // Improved heuristic: Calculate "visual length" instead of char count
    const totalLength = question.options.reduce((sum, opt) => {
        if (!opt.content) return sum;

        let content = opt.content;
        // 1. Remove latex delimiters
        content = content.replace(/\$/g, '');
        // 2. Replace common latex commands with single char (commands usually take vertical space or special symbols)
        // e.g. \frac, \sqrt, \alpha match \[a-zA-Z]+
        content = content.replace(/\\[a-zA-Z]+/g, 'M');
        // 3. Remove structural latex chars
        content = content.replace(/[{}_^]/g, '');
        // 4. Compress whitespace
        content = content.replace(/\s+/g, ' ');

        return sum + content.length;
    }, 0);

    // Adjusted thresholds based on "visual chars"
    if (totalLength <= 50) {
        return 'layout-1-line';
    } else if (totalLength <= 100) {
        return 'layout-2-line';
    } else {
        return 'layout-4-line';
    }
}

// 教材版本数据
const textbookVersions = ref([]);
const selectedVersion = ref(null);

// 教材数据
const allTextbooks = ref([]);
const selectedTextbook = ref(null);

// 过滤后的教材（根据版本）
const filteredTextbooks = computed(() => {
    if (!selectedVersion.value) return allTextbooks.value;
    return allTextbooks.value.filter(t => t.textbookVersionId === selectedVersion.value);
});

const chapters = ref([])
// 题目数据
const questions = ref([])


// 筛选条件数据
const scenes = ref(["月考", "期中", "期末", "模拟", "真题", "强基", "竞赛", "单元测", "作业"])
const questionTypes = ref(["单选题", "多选题", "填空题", "解答题", "解答题", "应用题"])
const difficulties = ref(["容易", "较易", "适中", "较难", "困难"])
const years = ref(["2025", "2024", "2023", "2022", "2021"])
const areas = ref(["全国", "北京市", "上海市", "天津市", "重庆市", "江苏省", "浙江省", "广东省", "湖北省", "山东省", "四川省", "湖南省", "河南省", "安徽省", "福建省", "陕西省", "辽宁省", "江西省", "河北省", "山西省", "吉林省", "黑龙江省", "云南省", "贵州省", "甘肃省", "海南省", "青海省"])
const grades = ref(["全部", "高一", "高二", "高三"])

const paperBasket = reactive([])
const basketDrawerVisible = ref(false)
const animatingQuestionId = ref(null)  // Track which question card is animating (adding)
const removingQuestionId = ref(null)  // Track which question card is animating (removing)
const floatingCardStyle = ref({})  // Style for the flying card animation

const showListDialog = ref(false)
const selectedQuestionForList = ref(null)

const openListDialog = (question) => {
    if (!requireLogin()) return
    selectedQuestionForList.value = question
    showListDialog.value = true
}

const loadBasket = async () => {
    // Only load basket if user is logged in
    if (!currentUserId.value) {
        return
    }
    try {
        const res = await api.get(`/api/basket?userId=${currentUserId.value}`)
        paperBasket.splice(0, paperBasket.length, ...res.data)
    } catch (err) {
        console.error('Failed to load basket', err)
    }
}

// Check if question is already in basket
function isInBasket(question) {
    return paperBasket.some(q => q.id === question.id)
}

// Toggle question in/out of basket
async function toggleBasket(question, event) {
    // Require login before any basket operation
    if (!requireLogin()) return

    if (isInBasket(question)) {
        await removeQuestionFromBasket(question, event)
    } else {
        await addToBasket(question, event)
    }
}

// Remove question from basket by question object
async function removeQuestionFromBasket(question, event) {
    try {
        // Trigger removing animation
        removingQuestionId.value = question.id

        // Create fly-out animation effect (from basket to button)
        if (event) {
            const button = event.currentTarget
            const buttonRect = button.getBoundingClientRect()
            const basket = document.querySelector('.floating-basket')
            if (basket) {
                const basketRect = basket.getBoundingClientRect()

                // Create a flying element that goes from basket to the card
                const flyingEl = document.createElement('div')
                flyingEl.className = 'flying-card-out'
                flyingEl.innerHTML = '<i class="fas fa-file-alt"></i>'
                flyingEl.style.cssText = `
                    position: fixed;
                    left: ${basketRect.left + basketRect.width / 2 - 20}px;
                    top: ${basketRect.top + basketRect.height / 2 - 20}px;
                    width: 40px;
                    height: 40px;
                    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 16px;
                    z-index: 10000;
                    pointer-events: none;
                    transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                    box-shadow: 0 4px 15px rgba(239, 68, 68, 0.5);
                `
                document.body.appendChild(flyingEl)

                // Add shake effect to basket
                basket.classList.add('basket-shake')

                // Trigger the animation (fly from basket to button)
                setTimeout(() => {
                    flyingEl.style.left = `${buttonRect.left}px`
                    flyingEl.style.top = `${buttonRect.top}px`
                    flyingEl.style.transform = 'scale(0.3) rotate(15deg)'
                    flyingEl.style.opacity = '0'
                }, 10)

                // Remove the flying element after animation
                setTimeout(() => {
                    flyingEl.remove()
                    basket.classList.remove('basket-shake')
                }, 500)
            }
        }

        await api.post('/api/basket/remove', {
            userId: currentUserId.value,
            questionId: question.id
        })
        const index = paperBasket.findIndex(q => q.id === question.id)
        if (index !== -1) {
            paperBasket.splice(index, 1)
        }
        ElNotification({
            title: '移除成功',
            message: `已从试题篮移除 (${paperBasket.length}道)`,
            type: 'success',
            duration: 2000,
        })

        // Reset animation state
        setTimeout(() => {
            removingQuestionId.value = null
        }, 600)
    } catch (err) {
        console.error('Failed to remove from basket', err)
        removingQuestionId.value = null
        ElNotification({
            title: '错误',
            message: '移除失败',
            type: 'error',
            duration: 2000,
        })
    }
}

async function addToBasket(question, event) {
    if (!paperBasket.find(q => q.id === question.id)) {
        try {
            // Trigger animation
            animatingQuestionId.value = question.id

            // Create fly animation effect
            if (event) {
                const button = event.currentTarget
                const buttonRect = button.getBoundingClientRect()
                const basket = document.querySelector('.floating-basket')
                if (basket) {
                    const basketRect = basket.getBoundingClientRect()

                    // Create a flying element
                    const flyingEl = document.createElement('div')
                    flyingEl.className = 'flying-card'
                    flyingEl.innerHTML = '<i class="fas fa-file-alt"></i>'
                    flyingEl.style.cssText = `
                        position: fixed;
                        left: ${buttonRect.left}px;
                        top: ${buttonRect.top}px;
                        width: 40px;
                        height: 40px;
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        border-radius: 8px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                        font-size: 16px;
                        z-index: 10000;
                        pointer-events: none;
                        transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.5);
                    `
                    document.body.appendChild(flyingEl)

                    // Trigger the animation
                    setTimeout(() => {
                        flyingEl.style.left = `${basketRect.left + basketRect.width / 2 - 20}px`
                        flyingEl.style.top = `${basketRect.top + basketRect.height / 2 - 20}px`
                        flyingEl.style.transform = 'scale(0.5)'
                        flyingEl.style.opacity = '0.8'
                    }, 10)

                    // Remove the flying element after animation
                    setTimeout(() => {
                        flyingEl.remove()
                        // Add a pulse effect to the basket
                        basket.classList.add('basket-pulse')
                        setTimeout(() => basket.classList.remove('basket-pulse'), 300)
                    }, 500)
                }
            }

            await api.post('/api/basket/add', {
                userId: currentUserId.value,
                questionId: question.id
            })
            paperBasket.push(question)
            ElNotification({
                title: '添加成功',
                message: `已添加到试题篮 (${paperBasket.length}道)`,
                type: 'success',
                duration: 2000,
            })

            // Reset animation state
            setTimeout(() => {
                animatingQuestionId.value = null
            }, 600)
        } catch (err) {
            console.error('Failed to add to basket', err)
            animatingQuestionId.value = null
            ElNotification({
                title: '错误',
                message: '添加失败',
                type: 'error',
                duration: 2000,
            })
        }
    } else {
        ElNotification({
            title: '提示',
            message: '该题目已在试题篮中',
            type: 'warning',
            duration: 2000,
        })
    }
}

async function removeFromBasket(index) {
    const question = paperBasket[index]
    try {
        await api.post('/api/basket/remove', {
            userId: currentUserId.value,
            questionId: question.id
        })
        paperBasket.splice(index, 1)
    } catch (err) {
        console.error('Failed to remove from basket', err)
    }
}

async function clearBasket() {
    try {
        await api.post('/api/basket/sync', {
            userId: currentUserId.value,
            questionIds: []
        })
        paperBasket.splice(0, paperBasket.length)
    } catch (err) {
        console.error('Failed to clear basket', err)
    }
}

function goToPaperComposition() {
    basketDrawerVisible.value = false
    router.push('/paper')
}

function getQuestionTypeText(type) {
    const map = { 1: '单选题', 2: '填空题', 3: '解答题', 4: '证明题', 5: '应用题', 6: '多选题' }
    return map[type] || '其他'
}

function getDifficultyText(diff) {
    const map = { 1: '容易', 2: '较易', 3: '适中', 4: '较难', 5: '困难' }
    return map[diff] || '未知'
}

function truncateContent(content, maxLen) {
    // Remove markdown/html formatting for preview
    const plainText = content.replace(/[#*`\[\]()]/g, '').replace(/<[^>]*>/g, '')
    if (plainText.length <= maxLen) return plainText
    return plainText.substring(0, maxLen) + '...'
}

// 筛选状态
const filter = reactive({
    scene: "all",
    type: "all",
    difficult: "all",
    year: "all",
    area: "all",
    grades: "all",
})

// Check if any filter is active
const hasActiveFilters = computed(() => {
    return filter.type !== 'all' || filter.difficult !== 'all' ||
        filter.year !== 'all' || filter.area !== 'all' || filter.grades !== 'all' || filter.scene !== 'all'
})

// Clear all filters
const clearFilters = () => {
    filter.scene = 'all'
    filter.type = 'all'
    filter.difficult = 'all'
    filter.year = 'all'
    filter.area = 'all'
    filter.grades = 'all'
}

// 其他状态
const searchQuery = ref("")
const onlyNew = ref(false)
const showAnswer = ref(false)
const activeSection = ref(null)
const activeChapter = ref(null)
const activeSectionTag = ref(null)
const activeTag = ref(null)
const activeKnowledgePoint = ref(null)
const sortOption = ref("newest")
const areaExpanded = ref(false)  // Control area filter expand/collapse
const filterCollapsed = ref(false)  // Control entire filter section collapse
const advancedFilterExpanded = ref(false)  // Control advanced filter section expand

// Check if any advanced filter is active
const hasAdvancedFilters = computed(() => {
    return filter.year !== 'all' || filter.area !== 'all'
})

// Fetch textbook versions from backend
const fetchTextbookVersions = async () => {
    try {
        const res = await getTextbookVersions();
        textbookVersions.value = res.data.versions || res.data || [];
        if (textbookVersions.value.length > 0) {
            selectedVersion.value = textbookVersions.value[0].id;
            fetchTextbooks();
        }
    } catch (error) {
        console.error('Failed to fetch textbook versions:', error);
    }
};

// Fetch textbooks from backend
const fetchTextbooks = async () => {
    try {
        const res = await api.get('/api/textbooks');
        allTextbooks.value = res.data.textbooks || res.data || [];
        if (filteredTextbooks.value.length > 0) {
            selectedTextbook.value = filteredTextbooks.value[0].id;
            fetchChaptersByTextbook();
        } else {
            selectedTextbook.value = null;
            chapters.value = [];
        }
    } catch (error) {
        console.error('Failed to fetch textbooks:', error);
    }
};

// Fetch chapters by textbook from backend
const fetchChaptersByTextbook = async () => {
    if (!selectedTextbook.value) {
        chapters.value = [];
        return;
    }
    try {
        const res = await api.get('/api/chapters/by-textbook', {
            params: { textbookId: selectedTextbook.value }
        });
        const rawChapters = res.data.chapters || [];

        // Load full sections with knowledge points for each chapter in parallel
        await Promise.all(rawChapters.map(async (chapter) => {
            if (!chapter.sections || chapter.sections.length === 0) return
            try {
                const secRes = await api.get('/api/sections/by-chapter', {
                    params: { chapterId: chapter.id }
                })
                const fullSections = secRes.data.sections || []
                for (const section of chapter.sections) {
                    const fullSection = fullSections.find(s => s.id === section.id)
                    if (fullSection) {
                        section.knowledgePoints = fullSection.knowledgePoints || []
                    }
                }
            } catch (e) {
                console.error('Failed to fetch sections for chapter', chapter.id, e)
            }
        }))

        chapters.value = rawChapters.map(ch => ({
            ...ch,
            collapsed: false,
            // Ensure sections have proper structure
            sections: (ch.sections || []).map(sec => ({
                ...sec,
                knowledgePoints: sec.knowledgePoints || []
            }))
        }));

    } catch (error) {
        console.error('Failed to fetch chapters:', error);
        chapters.value = [];
    }
};

// Handle version change
const onVersionChange = () => {
    // When version changes, filteredTextbooks updates automatically.
    // We just need to reset selectedTextbook to the first one of the new version.
    if (filteredTextbooks.value.length > 0) {
        selectedTextbook.value = filteredTextbooks.value[0].id;
        fetchChaptersByTextbook();
    } else {
        selectedTextbook.value = null;
        chapters.value = [];
    }
};

// Handle textbook change
const onTextbookChange = () => {
    fetchChaptersByTextbook();
};

// const pageSize = ref(5)


const total = ref(0)
const page = ref(1)
const pageSize = 10

const fetchQuestions = async () => {
    await fetchQuestionsByFilter()
}

const getQuestionTypeId = (type) => {
    const typeMap = {
        '全部': 0,
        '单选题': 1,
        '多选题': 6,
        '填空题': 2,
        '解答题': 3,
        '证明题': 4,
        '应用题': 5
    };
    return typeMap[type] || type;
};

const getDifficultId = (type) => {
    const typeMap = {
        '全部': 0,
        '容易': 1,
        '较易': 2,
        '适中': 3,
        '较难': 4,
        '困难': 5,
    };
    return typeMap[type] || type;
};

const getGradeId = (type) => {
    console.log("getGradeId: ", type);
    const typeMap = {
        '全部': 0,
        '高一': 10,
        '高二': 11,
        '高三': 12,
    };
    return typeMap[type] || type;
};

const fetchQuestionsByFilter = async () => {
    //   const params = {
    //     page: page.value,
    //     pageSize,
    //     tagIds: filters.tagIds.join(','),
    //     knowledgePointIds: filters.knowledgePointIds.join(','),
    //     provinceIds: filters.provinceIds.join(','),
    //     year: filters.year,
    //     type: filters.type,
    //     difficult: filters.difficult
    //   }
    const params = {
        page: page.value,
        page_size: pageSize,
        region: filter.area === "all" ? "0" : filter.area,
        year: filter.year === 'all' ? "0" : filter.year,
        type: filter.type === "all" || filter.type === "全部" ? 0 : getQuestionTypeId(filter.type),
        difficult: filter.difficult === "all" || filter.difficult === "全部" ? 0 : getDifficultId(filter.difficult),
        grades: filter.grades === "all" || filter.grades === "全部" ? 0 : getGradeId(filter.grades),
        scene: filter.scene === "all" ? "" : filter.scene,
        knowledgePointId: activeKnowledgePoint.value || 0,
        chapterId: activeSection.value ? 0 : (activeChapter.value || 0), // If section is selected, ignore chapter? Or pass both? Backend handles independently.
        sectionId: activeSection.value || 0,
        keyword: searchQuery.value
    }
    try {
        const res = await api.get('/api/questions', { params })
        questions.value = res.data.questions || []
        total.value = res.data.total || 0
    } catch (error) {
        console.error('Failed to fetch questions by filter:', error)
        questions.value = []
        total.value = 0
    }
}

const debounce = (fn, wait = 250) => {
    let timer = null
    return (...args) => {
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => fn(...args), wait)
    }
}

const debouncedFetchQuestionsByFilter = debounce(() => {
    page.value = 1
    fetchQuestionsByFilter()
})

let searchDebounceTimer = null
const triggerSearch = () => {
    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer)
        searchDebounceTimer = null
    }
    page.value = 1
    fetchQuestionsByFilter()
}

watch(
    [
        () => filter.scene,
        () => filter.type,
        () => filter.difficult,
        () => filter.year,
        () => filter.area,
        () => filter.grades
    ],
    () => {
        debouncedFetchQuestionsByFilter()
    }
)

watch(
    () => searchQuery.value,
    () => {
        if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
        searchDebounceTimer = setTimeout(() => {
            page.value = 1
            fetchQuestionsByFilter()
            searchDebounceTimer = null
        }, 300)
    }
)

const handlePageChange = (p) => {
    page.value = p
    fetchQuestionsByFilter()
}

// 选择章节
const selectChapter = (chapter) => {
    activeChapter.value = activeChapter.value === chapter.id ? null : chapter.id;
    activeSection.value = null;
    activeKnowledgePoint.value = null;
    page.value = 1;
    fetchQuestionsByFilter();
}

// 选择小节
const selectSection = (section) => {
    activeSection.value = activeSection.value === section.id ? null : section.id;
    activeChapter.value = null; // Clear chapter selection if section is selected (optional, depends on UX)
    activeKnowledgePoint.value = null;
    page.value = 1;
    fetchQuestionsByFilter();
}

// 选择标签
const selectTag = (tag) => {
    activeTag.value = activeTag.value === tag ? null : tag;
    activeSection.value = null;
}

// 选择知识点
const selectKnowledgePoint = (kp) => {
    activeKnowledgePoint.value = activeKnowledgePoint.value === kp.id ? null : kp.id;
    // activeSection.value = null; // Keep section active? Maybe.
    page.value = 1;
    fetchQuestionsByFilter();
}

// 切换答案显示
const toggleAnswer = (question) => {
    question.showAnswer = !question.showAnswer;
}

// 初始化MathJax渲染
const renderMath = () => {
    if (window.MathJax) {
        window.MathJax.typeset();
    }
}

// 初始化时渲染数学公式
onMounted(() => {
    renderMath();
    fetchTextbookVersions();
    loadBasket();
    fetchQuestionsByFilter();
});

onBeforeUnmount(() => {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
})

</script>

<style scoped>
* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: 'Noto Sans SC', sans-serif;
    background-color: #f5f7fa;
    color: #333;
    line-height: 1.6;
}

.option-container {
    margin-top: 8px;
    gap: 8px;
}

.layout-1-line {
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
}

.layout-2-line {
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 8px;
    column-gap: 16px;
}

.layout-4-line {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.option-item {
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #fafafa;
}

.textbook-selector {
    display: flex;
    align-items: center;
    gap: 15px;
}



.app-container {
    display: flex;
    min-height: 100vh;
    background: #f5f7fa;
    gap: 0;
}

/* 左侧目录样式 */
.sidebar {
    width: 280px;
    background: #fbfbfd;
    color: #333;
    padding: 20px 0;
    overflow-y: auto;
    border-right: 1px solid rgba(0, 0, 0, 0.05);
    z-index: 10;
    flex-shrink: 0;
    position: sticky;
    top: 64px;
    height: calc(100vh - 64px);
}

.logo-container {
    padding: 0 24px 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    margin-bottom: 10px;
}

.logo {
    font-size: 18px;
    font-weight: 700;
    color: #1d1d1f;
    display: flex;
    align-items: center;
    letter-spacing: -0.5px;
}

.logo i {
    margin-right: 10px;
    color: #007aff;
    font-size: 20px;
}

.directory-tree {
    padding: 0 15px;
}

/* 选择器区域样式 */
.selector-section {
    margin-bottom: 15px;
    padding: 12px;
    background-color: #f5f7fa;
    border-radius: 6px;
}

.selector-section label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #606266;
}

.selector-section select {
    width: 100%;
    padding: 8px 12px;
    background-color: #fff;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    color: #333;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s;
}

.selector-section select:hover {
    border-color: #c0c4cc;
}

.selector-section select:focus {
    outline: none;
    border-color: #409eff;
}

.selector-section select option {
    background-color: #fff;
    color: #333;
}

.textbook-title {
    font-size: 16px;
    font-weight: 600;
    padding: 12px 15px;
    margin-bottom: 10px;
    background-color: #e6f1ff;
    border-radius: 6px;
    display: flex;
    align-items: center;
    color: #2c5282;
}

.textbook-title i {
    margin-right: 8px;
    color: #409eff;
}

.chapter {
    margin-bottom: 15px;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #e5e5e5;
}

.chapter-header {
    background-color: #f5f7fa;
    padding: 12px 15px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all 0.3s;
    color: #333;
}

.chapter-header:hover {
    background-color: #ecf5ff;
}

.chapter-header.active {
    background-color: #409eff;
    color: #fff;
}

.chapter-header i {
    margin-right: 8px;
    font-size: 14px;
    transition: transform 0.3s;
}

.chapter-header.collapsed i {
    transform: rotate(-90deg);
}

.sections {
    background-color: #fafafa;
    border-radius: 0 0 6px 6px;
}

.section {
    padding: 10px 15px 10px 35px;
    border-bottom: 1px solid #ebeef5;
    position: relative;
    cursor: pointer;
    transition: all 0.2s;
    color: #606266;
}

.section:hover {
    background-color: #ecf5ff;
}

.section.active {
    background-color: #ecf5ff;
    color: #409eff;
    font-weight: 500;
}

.section::before {
    content: "";
    position: absolute;
    left: 20px;
    top: 16px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #409eff;
}

.tags {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 8px;
}

.tag {
    background-color: #f0f9ff;
    color: #409eff;
    font-size: 12px;
    padding: 3px 8px;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid #d9ecff;
}

.tag:hover {
    background-color: #ecf5ff;
    border-color: #b3d8ff;
}

.tag.active {
    background-color: #409eff;
    color: white;
    border-color: #409eff;
}

/* 右侧内容区域样式 */
.main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    margin-left: 15px;
    margin-right: 15px;
    margin-top: 15px;
    margin-bottom: 15px;
    background: #f5f7fa;
    border-radius: 12px;
}

.top-bar {
    background-color: white;
    padding: 15px 25px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 5;
}

.search-box {
    display: flex;
    align-items: center;
    background: #f5f5f7;
    border-radius: 10px;
    padding: 8px 12px;
    width: 300px;
    transition: all 0.3s ease;
}

.search-box:focus-within {
    background: #fff;
    box-shadow: 0 0 0 4px rgba(0, 122, 255, 0.1);
    width: 320px;
}

.search-box i {
    color: #86868b;
    margin-right: 8px;
    font-size: 14px;
}

.search-box input {
    border: none;
    background: transparent;
    outline: none;
    font-size: 14px;
    color: #1d1d1f;
    width: 100%;
}

.search-box input::placeholder {
    color: #86868b;
}

.filter-section {
    background-color: white;
    padding: 15px 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    margin-bottom: 15px;
}

.filter-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-weight: 600;
    font-size: 15px;
    color: #1e293b;
}

.filter-title-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.filter-title-left i {
    color: #667eea;
}

.filter-title-right {
    display: flex;
    align-items: center;
}

.filter-toggle-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: normal;
    color: #64748b;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 4px;
    transition: all 0.2s;
}

.filter-toggle-btn:hover {
    background: #f1f5f9;
    color: #334155;
}

.filter-toggle-btn i {
    font-size: 12px;
}

.clear-filters {
    font-size: 13px;
    font-weight: normal;
    color: #ef4444;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 4px;
    transition: all 0.2s;
}

.clear-filters:hover {
    background: #fef2f2;
}

/* Filter body container */
.filter-body {
    transition: all 0.3s ease;
}

/* Collapsed state */
.filter-section.filter-collapsed .filter-title {
    margin-bottom: 0;
}

/* Advanced filter section styles */
.advanced-filter-section {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #e2e8f0;
}

.advanced-filter-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    cursor: pointer;
    user-select: none;
}

.advanced-filter-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    transition: color 0.2s;
}

.advanced-filter-toggle:hover .advanced-filter-label {
    color: #334155;
}

.advanced-filter-label i {
    font-size: 12px;
    color: #94a3b8;
}

.advanced-filter-hint {
    font-size: 12px;
    color: #f59e0b;
    font-weight: 500;
}

.advanced-filter-body {
    padding-left: 12px;
    margin-top: 4px;
    transition: all 0.3s ease;
}

.filter-row {
    display: flex;
    align-items: flex-start;
    margin-bottom: 10px;
    padding: 6px 0;
    border-bottom: 1px solid #f1f5f9;
}

.filter-row:last-of-type {
    border-bottom: none;
}

.filter-label {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
    width: 50px;
    flex-shrink: 0;
    padding-top: 6px;
}

.filter-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex: 1;
}

.filter-pill {
    padding: 6px 16px;
    font-size: 13px;
    color: #1d1d1f;
    background: #f5f5f7;
    border-radius: 18px;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
    border: none;
}

.filter-pill:hover {
    background: #e8e8ed;
    color: #1d1d1f;
}

.filter-pill.active {
    background: #007aff;
    color: white;
    font-weight: 500;
    box-shadow: 0 2px 8px rgba(0, 122, 255, 0.25);
}

/* Collapsible filter row */
.filter-row-collapsible {
    align-items: flex-start;
}

.filter-pills-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.filter-pills.collapsed {
    max-height: 72px;
    overflow: hidden;
    position: relative;
}

.filter-pills.collapsed::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(transparent, white);
    pointer-events: none;
}

.expand-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    font-size: 12px;
    color: #667eea;
    cursor: pointer;
    padding: 4px 10px;
    background: #f5f3ff;
    border-radius: 12px;
    width: fit-content;
    transition: all 0.2s;
}

.expand-btn:hover {
    background: #ede9fe;
}

.expand-btn i {
    font-size: 10px;
}

select {
    background: transparent;
    border: none;
    font-size: 14px;
    color: #1e293b;
    outline: none;
    cursor: pointer;
}

.additional-options {
    display: flex;
    align-items: center;
    margin-top: 15px;
    flex-wrap: wrap;
    gap: 15px;
}

.checkbox-option {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #475569;
}

.checkbox-option input {
    margin-right: 6px;
}

.stats {
    font-size: 14px;
    color: #64748b;
    margin-top: 15px;
    display: flex;
    align-items: center;
}

.stats i {
    margin-right: 8px;
}

/* 题目列表样式 */
.questions-container {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
}

.questions-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 14px 18px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 16px rgba(16, 24, 40, 0.04);
}

.questions-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 17px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: 0.2px;
}

.questions-title i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    font-size: 13px;
    color: #fff;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    box-shadow: 0 3px 8px rgba(99, 102, 241, 0.3);
}

.sort-options {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #64748b;
}

.sort-options select {
    appearance: none;
    -webkit-appearance: none;
    margin-left: 0;
    padding: 7px 32px 7px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background-color: #f8fafc;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='7' viewBox='0 0 10 7'%3E%3Cpath d='M1 1.5L5 5.5L9 1.5' fill='none' stroke='%2394a3b8' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 11px center;
    font-size: 13px;
    color: #334155;
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s;
}

.sort-options select:hover {
    border-color: #c7d2fe;
    background-color: #f5f7ff;
}

.sort-options select:focus {
    border-color: #6366f1;
    background-color: #fff;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}

.question-list {
    display: grid;
    /* grid-template-columns: repeat(auto-fill, minmax(500px, 1fr)); */
    grid-template-columns: repeat(auto-fill, minmax(600px, 1fr));
    gap: 16px;
    align-content: start;
}

@media (max-width: 1200px) {
    .question-list {
        grid-template-columns: 1fr;
    }
}

/* 空状态 */
.empty-questions {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 72px 20px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.empty-questions i {
    font-size: 40px;
    color: #cbd5e1;
    margin-bottom: 14px;
}

.empty-questions p {
    margin: 0 0 6px;
    font-size: 15px;
    font-weight: 600;
    color: #64748b;
}

.empty-questions span {
    font-size: 13px;
    color: #94a3b8;
}

/* Question Card (quesroot) */
.quesroot {
    background: #fff;
    border: 1px solid #eef1f6;
    margin-bottom: 0;
    padding: 0;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    transition: box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
}

.quesroot:hover {
    border-color: rgba(99, 102, 241, 0.35);
    box-shadow: 0 10px 28px rgba(16, 24, 40, 0.1);
    transform: translateY(-2px);
}

/* Header Area */
.ques-additional {
    background: #fff;
    padding: 0;
    border-bottom: 1px solid #f1f5f9;
    border-radius: 12px 12px 0 0;
}

.top-msg {
    padding: 0;
    margin-bottom: 0;
}

.addi-msg.ques-src {
    display: inline-block;
    max-width: 100%;
    padding: 5px 14px;
    background: linear-gradient(135deg, #eef2ff, #e0e7ff);
    color: #4f46e5;
    font-size: 12px;
    font-weight: 600;
    border-bottom-right-radius: 12px;
    border-top-left-radius: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.msg-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 10px 20px;
    font-size: 12px;
    color: #94a3b8;
}

.left-msg {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    min-width: 0;
}

/* 题型 / 难度 胶囊标签 */
.left-msg .addi-info {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 999px;
    background: #f1f5f9;
}

.info-cnt {
    color: #475569;
    font-weight: 500;
}

/* 胶囊化后不再需要竖线分隔 */
.left-msg > .gap {
    display: none;
}

.gap {
    color: #e0e0e0;
    font-weight: normal;
    margin: 0 4px;
}

.knowledge-list-wrapper {
    display: inline-flex;
    min-width: 0;
}

.knowledge-list {
    display: flex;
    gap: 6px;
}

.knowledge-item {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 999px;
    background: #f8fafc;
    border: 1px solid #eef2f7;
    color: #64748b;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.18s ease;
}

.knowledge-item:hover {
    color: #4f46e5;
    background: #eef2ff;
    border-color: #c7d2fe;
}

.right-msg {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.right-msg .special-tag {
    font-size: 11px;
    padding: 3px 9px;
    border-radius: 999px;
    font-weight: 500;
    background-color: #fff7e6;
    color: #d48806;
    border: 1px solid #ffe0a3;
}

.special-tag.default-blue {
    background-color: #eef6ff;
    color: #2b7ce3;
    border: 1px solid #cfe6ff;
}

/* Content Area */
.quesdiv {
    padding: 18px 20px 20px;
}

.exam-item__cnt {
    font-size: 15px;
    line-height: 1.85;
    color: #1f2937;
    cursor: pointer;
}

.question-content-wrapper {
    display: flex;
    align-items: flex-start;
}

.question-index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 7px;
    margin-right: 10px;
    margin-top: 2px;
    border-radius: 8px;
    background: linear-gradient(135deg, #eef2ff, #e0e7ff);
    color: #4f46e5;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
}

.question-body {
    flex: 1;
    min-width: 0;
}

/* Fix Markdown Table Overflow */
:deep(.md-editor-preview table) {
    display: block;
    width: 100%;
    overflow-x: auto;
    word-break: keep-all;
}

/* Markdown editor overrides for questions */
.question-body :deep(.md-editor-preview-wrapper),
.question-body :deep(.md-editor-preview) {
    padding: 0 !important;
    overflow: visible !important;
    height: auto !important;
    background: transparent !important;
}

.question-body :deep(p) {
    margin: 0 !important;
}

.exam-item__opt.answer-section {
    margin-top: 16px;
    padding: 16px;
    background: #f8fafc;
    border: 1px solid #eef2f7;
    border-left: 3px solid #6366f1;
    border-radius: 4px 10px 10px 4px;
}

.answer-title {
    font-size: 13px;
    font-weight: 700;
    color: #475569;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    letter-spacing: 0.2px;
}

.answer-title i {
    color: #f59e0b;
    margin-right: 6px;
}

.answer-content + .answer-title {
    margin-top: 14px;
}

.answer-content {
    font-size: 14px;
    line-height: 1.75;
    color: #334155;
    margin-bottom: 12px;
}

.answer-content:last-child {
    margin-bottom: 0;
}

/* Footer Area */
.exam-item__info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    padding: 12px 20px;
    background: #fcfcfe;
    border-top: 1px solid #f1f5f9;
    border-radius: 0 0 12px 12px;
}

.info-msgs {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #94a3b8;
    min-width: 0;
    flex: 1;
}

.info-msgs .no-bound {
    padding: 2px 8px;
    border-radius: 999px;
    background: #ecfdf5;
    color: #059669;
    font-size: 11px;
    font-weight: 500;
}

.info-msgs > .gap {
    display: none;
}

.info-txt.source-text {
    max-width: 230px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-block;
    vertical-align: bottom;
}

.ctrl-box {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
}

.ctrl-btn {
    font-size: 12.5px;
    color: #64748b;
    cursor: pointer;
    text-decoration: none;
    padding: 5px 10px;
    border-radius: 7px;
    white-space: nowrap;
    transition: color 0.18s ease, background-color 0.18s ease;
}

.ctrl-btn:hover {
    color: #4f46e5;
    background: #eef2ff;
}

/* 加入试题篮主按钮 */
.ctrl-box .add-exam-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 7px 16px;
    margin-left: 6px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: #fff !important;
    border: none;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 500;
    box-shadow: 0 3px 10px rgba(99, 102, 241, 0.28);
    transition: box-shadow 0.22s ease, transform 0.22s ease, background 0.22s ease;
}

.ctrl-box .add-exam-btn:hover {
    background: linear-gradient(135deg, #4f46e5, #4338ca);
    color: #fff !important;
    transform: translateY(-1px);
    box-shadow: 0 5px 16px rgba(99, 102, 241, 0.38);
}

.ctrl-box .add-exam-btn.is-in-basket {
    background: #f1f5f9;
    color: #64748b !important;
    border: 1px solid #e2e8f0;
    box-shadow: none;
}

.ctrl-box .add-exam-btn.is-in-basket:hover {
    background: #fef2f2;
    color: #ef4444 !important;
    border-color: #fecaca;
    transform: none;
    box-shadow: none;
}

/* Similar Questions Dialog */
.similar-question-item {
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
}

.similar-question-item:last-child {
    border-bottom: none;
}

.similar-q-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.similar-q-meta {
    font-size: 12px;
    color: #999;
}

.similar-q-content {
    margin-bottom: 8px;
}

.similar-q-content :deep(.md-editor-preview-wrapper),
.similar-q-content :deep(.md-editor-preview) {
    padding: 0 !important;
    overflow: visible !important;
    height: auto !important;
    background: transparent !important;
}

.similar-q-actions {
    display: flex;
    gap: 12px;
}

/* 底部功能区 */
.bottom-actions {
    margin-top: 18px;
    background: #fff;
    padding: 14px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 16px rgba(16, 24, 40, 0.04);
}

.bottom-actions :deep(.el-pagination.is-background .el-pager li) {
    border-radius: 8px;
    font-weight: 500;
}

.bottom-actions :deep(.el-pagination.is-background .el-pager li.is-active) {
    background: linear-gradient(135deg, #6366f1, #4f46e5);
}

.bottom-actions :deep(.el-pagination.is-background .btn-prev),
.bottom-actions :deep(.el-pagination.is-background .btn-next) {
    border-radius: 8px;
}

.practice-options {
    display: flex;
    gap: 15px;
}

.practice-btn {
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all 0.2s;
}

.practice-btn i {
    margin-right: 8px;
}

.primary-btn {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
}

.primary-btn:hover {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    box-shadow: 0 4px 8px rgba(37, 99, 235, 0.3);
}

.secondary-btn {
    background: #f1f5f9;
    color: #475569;
}

.secondary-btn:hover {
    background: #e2e8f0;
}

.pagination {
    display: flex;
    align-items: center;
    gap: 10px;
}

.pagination-btn {
    width: 36px;
    height: 36px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f1f5f9;
    color: #475569;
    cursor: pointer;
    transition: all 0.2s;
}

.pagination-btn:hover {
    background: #e2e8f0;
}

.pagination-btn.active {
    background: #3b82f6;
    color: white;
}

/* Floating Question Basket */
.floating-basket {
    position: fixed;
    right: 20px;
    bottom: 100px;
    width: 60px;
    height: 80px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    transition: all 0.3s ease;
    z-index: 1000;
}

.floating-basket:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
}

.basket-icon {
    font-size: 22px;
    color: white;
}

.basket-count {
    position: absolute;
    top: -5px;
    right: -5px;
    background: #ef4444;
    color: white;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    border: 2px solid white;
}

.basket-label {
    font-size: 11px;
    color: white;
    margin-top: 4px;
    font-weight: 500;
}

/* Basket Drawer Styles */
.basket-drawer-content {
    padding: 10px;
    height: 100%;
}

.empty-basket {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
    color: #9ca3af;
}

.empty-basket i {
    font-size: 48px;
    margin-bottom: 15px;
}

.empty-basket p {
    font-size: 16px;
    margin: 0 0 5px 0;
    color: #6b7280;
}

.empty-basket span {
    font-size: 13px;
}

.basket-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 15px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 10px;
    margin-bottom: 15px;
}

.basket-summary strong {
    color: #667eea;
    font-size: 18px;
}

.basket-items {
    max-height: calc(100vh - 280px);
    overflow-y: auto;
}

.basket-item {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
    margin-bottom: 10px;
    transition: all 0.2s;
}

.basket-item:hover {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.basket-item-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.basket-item-num {
    width: 24px;
    height: 24px;
    background: #667eea;
    color: white;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
}

.basket-item-type {
    background: #e0e7ff;
    color: #4338ca;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
}

.basket-item-diff {
    background: #fef3c7;
    color: #b45309;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    margin-right: auto;
}

.basket-item-content {
    font-size: 13px;
    color: #6b7280;
    line-height: 1.5;
}

.basket-actions {
    position: absolute;
    bottom: 20px;
    left: 20px;
    right: 20px;
}

.basket-actions .el-button {
    width: 100%;
}

/* Animation for adding to basket button */
.action-button.adding {
    animation: button-pulse 0.6s ease;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    color: white !important;
}

@keyframes button-pulse {
    0% {
        transform: scale(1);
    }

    25% {
        transform: scale(0.95);
    }

    50% {
        transform: scale(1.05);
    }

    75% {
        transform: scale(0.98);
    }

    100% {
        transform: scale(1);
    }
}

/* Basket pulse animation when item is added */
.floating-basket.basket-pulse {
    animation: basket-bounce 0.3s ease;
}

@keyframes basket-bounce {
    0% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.2);
    }

    100% {
        transform: scale(1);
    }
}

/* TransitionGroup animations for basket items */
.basket-list-enter-active {
    animation: slide-in 0.4s ease-out;
}

.basket-list-leave-active {
    animation: slide-out 0.3s ease-in;
    position: absolute;
    width: calc(100% - 24px);
}

.basket-list-move {
    transition: transform 0.3s ease;
}

@keyframes slide-in {
    0% {
        opacity: 0;
        transform: translateX(50px) scale(0.8);
    }

    60% {
        opacity: 1;
        transform: translateX(-10px) scale(1.02);
    }

    100% {
        transform: translateX(0) scale(1);
    }
}

@keyframes slide-out {
    0% {
        opacity: 1;
        transform: translateX(0) scale(1);
    }

    100% {
        opacity: 0;
        transform: translateX(50px) scale(0.8);
    }
}

/* Question card animation when adding to basket */
.question-card.card-adding {
    animation: card-highlight 0.5s ease;
}

@keyframes card-highlight {
    0% {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }

    50% {
        box-shadow: 0 0 20px rgba(102, 126, 234, 0.4);
        border-color: #667eea;
    }

    100% {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    }
}

/* Animation for removing from basket button */
.action-button.removing {
    animation: button-shake 0.5s ease;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
    color: white !important;
}

@keyframes button-shake {

    0%,
    100% {
        transform: translateX(0);
    }

    10%,
    30%,
    50%,
    70%,
    90% {
        transform: translateX(-3px);
    }

    20%,
    40%,
    60%,
    80% {
        transform: translateX(3px);
    }
}

/* Basket shake animation when item is removed */
.floating-basket.basket-shake {
    animation: basket-shake 0.4s ease;
}

@keyframes basket-shake {

    0%,
    100% {
        transform: translateX(0) rotate(0deg);
    }

    25% {
        transform: translateX(-5px) rotate(-5deg);
    }

    50% {
        transform: translateX(5px) rotate(5deg);
    }

    75% {
        transform: translateX(-3px) rotate(-3deg);
    }
}

.option-item-wrapper {
    display: flex;
    align-items: flex-start;
    margin-bottom: 3px;
}

.option-content {
    flex: 1;
}

/* Force markdown paragraph to be inline */
.option-content :deep(p) {
    margin: 0 !important;
    display: inline-block !important;
}

/* Force MdEditor wrapper to be inline */
.option-content :deep(.md-editor),
.option-content :deep(.md-editor-preview-wrapper),
.option-content :deep(.md-editor-preview) {
    display: inline-block !important;
    padding: 0 !important;
    overflow: hidden !important;
    background: transparent !important;
    border: none !important;
}

.option-label {
    margin-right: 5px;
    white-space: nowrap;
}

/* Apple Style Drawer */
:deep(.apple-drawer) {
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px);
    border-radius: 20px 0 0 20px;
    box-shadow: -5px 0 30px rgba(0, 0, 0, 0.1);
}

:deep(.apple-drawer .el-drawer__body) {
    padding: 0;
    display: flex;
    flex-direction: column;
}

.drawer-header {
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.drawer-title {
    font-size: 20px;
    font-weight: 700;
    color: #1d1d1f;
    letter-spacing: -0.5px;
}

.drawer-close {
    width: 30px;
    height: 30px;
    background: #f5f5f7;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #86868b;
    transition: all 0.2s;
}

.drawer-close:hover {
    background: #e8e8ed;
    color: #1d1d1f;
}

.clear-btn-apple {
    font-size: 13px;
    color: #ff3b30;
    cursor: pointer;
    padding: 6px 12px;
    border-radius: 15px;
    background: rgba(255, 59, 48, 0.1);
    transition: all 0.2s;
    font-weight: 500;
}

.clear-btn-apple:hover {
    background: rgba(255, 59, 48, 0.2);
}

.remove-btn-apple {
    color: #ff3b30;
    cursor: pointer;
    font-size: 16px;
    opacity: 0.6;
    transition: all 0.2s;
    margin-left: auto;
}

.remove-btn-apple:hover {
    opacity: 1;
    transform: scale(1.1);
}

.basket-drawer-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.basket-items {
    flex: 1;
    overflow-y: auto;
    padding: 10px 20px;
}

.basket-summary {
    padding: 15px 20px;
    background: #fbfbfd;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: #86868b;
}

.basket-summary strong {
    color: #1d1d1f;
}

/* Limit image size in question content */
:deep(.question-content img),
:deep(.md-editor-preview img) {
    max-width: min(100%, 420px) !important;
    width: auto !important;
    height: auto !important;
    max-height: 180px;
    object-fit: contain;
    margin: 5px 0;
    border-radius: 4px;
    display: block;
}
</style>
